import type { Dealer } from "../types";
import { haversineDistance, getDistanceKey } from "./distance";

type DistanceCache = Record<
  string,
  { distanceText: string; distanceValue: number; durationText?: string }
>;

type LatLng = { lat: number; lng: number };

const isFiniteNumber = (value: number) =>
  typeof value === "number" && Number.isFinite(value);

export const selectDistanceMatrixCandidates = ({
  origin,
  candidates,
  selectedDealerId,
  distanceCache,
  maxDestinations = 3,
  calculationMode = "manual",
}: {
  origin: LatLng;
  candidates: Dealer[];
  selectedDealerId: string | null;
  distanceCache: DistanceCache;
  maxDestinations?: number;
  calculationMode?: "automatic" | "manual";
}): Dealer[] => {
  const withApprox = candidates
    .filter(
      (w) => isFiniteNumber(w.latitude) && isFiniteNumber(w.longitude)
    )
    .map((w) => ({
      ...w,
      approxDistance: haversineDistance(
        origin.lat,
        origin.lng,
        w.latitude,
        w.longitude
      ),
    }))
    .sort((a, b) => a.approxDistance - b.approxDistance);

  if (selectedDealerId && calculationMode === "automatic") {
    const selectedWorkshop = withApprox.find(
      (w) => w.dealerId === selectedDealerId
    );
    if (!selectedWorkshop) {
      return [];
    }

    const selectedKey = getDistanceKey(origin, {
      lat: selectedWorkshop.latitude,
      lng: selectedWorkshop.longitude,
    });

    return distanceCache[selectedKey] ? [] : [selectedWorkshop];
  }

  const closest: Dealer[] = [];

  if (selectedDealerId && calculationMode === "manual") {
    const selectedWorkshop = withApprox.find(
      (w) => w.dealerId === selectedDealerId
    );
    if (selectedWorkshop) {
      const selectedKey = getDistanceKey(origin, {
        lat: selectedWorkshop.latitude,
        lng: selectedWorkshop.longitude,
      });
      if (!distanceCache[selectedKey]) {
        closest.push(selectedWorkshop);
      }
    }
  }

  for (const workshop of withApprox) {
    if (closest.length >= maxDestinations) break;

    if (selectedDealerId && workshop.dealerId === selectedDealerId) continue;

    const key = getDistanceKey(origin, {
      lat: workshop.latitude,
      lng: workshop.longitude,
    });
    if (!distanceCache[key]) {
      closest.push(workshop);
    }
  }

  return closest;
};

export const shouldAutoCalculateDistances = ({
  position,
  distanceCalculated,
  isLoadingWorkshops,
  rawWorkshopsLength,
  isInitialPositionLoad,
}: {
  position: unknown;
  distanceCalculated: boolean;
  isLoadingWorkshops: boolean;
  rawWorkshopsLength: number;
  isInitialPositionLoad: boolean;
}): boolean =>
  Boolean(position) &&
  !distanceCalculated &&
  !isLoadingWorkshops &&
  rawWorkshopsLength > 0 &&
  isInitialPositionLoad;

export const getDistanceCalculationLogType = ({
  isAutomatic,
  distanceCalculated,
  isCalculating,
}: {
  isAutomatic: boolean;
  distanceCalculated: boolean;
  isCalculating: boolean;
}): "manual" | "automatic" | null => {
  if (distanceCalculated) {
    return isAutomatic ? null : "manual";
  }
  if (isCalculating) return null;
  return isAutomatic ? "automatic" : "manual";
};
